import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { BriefProvider } from "@/context/BriefContext";
import { AuthProvider } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ExecHeader, ExecFooter } from "@/components/ExecChrome";
import { BriefModal } from "@/components/BriefModal";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute, AdminRoute } from "@/components/ProtectedRoute";
import { captureUTM, trackPageView } from "@/lib/analytics";
import { LEGAL_LINKS } from "@/lib/legalLinks";

import Home from "@/pages/Home";
import Work from "@/pages/Work";
import CaseStudyPage from "@/pages/CaseStudyPage";
import Services from "@/pages/Services";
import Sprint from "@/pages/Sprint";
import Intake from "@/pages/Intake";
import About from "@/pages/About";
import Refer from "@/pages/Refer";
import Resources from "@/pages/Resources";
import FieldNote from "@/pages/FieldNote";
import FrameworkPage from "@/pages/FrameworkPage";
import Scorecard from "@/pages/Scorecard";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import ExecutivePreview from "@/pages/ExecutivePreview";
import Login from "@/pages/Login";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import TheTablePublic from "@/pages/TheTablePublic";
import Welcome from "@/pages/Welcome";
import ExecutiveProduction from "@/pages/ExecutiveProduction";
import PublicSector from "@/pages/PublicSector";
import ForSegment from "@/pages/ForSegment";
import Search from "@/pages/Search";
import NotFound from "@/pages/NotFound";

import { HubLayout } from "@/pages/hub/HubLayout";
import Dashboard from "@/pages/hub/Dashboard";
import Deliverables from "@/pages/hub/Deliverables";
import PacketArchive from "@/pages/hub/PacketArchive";
import HubEvents from "@/pages/hub/HubEvents";
import TheTable from "@/pages/hub/TheTable";
import Cohort from "@/pages/hub/Cohort";
import Account from "@/pages/hub/Account";

import { AdminLayout } from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminSiteCopy from "@/pages/admin/AdminSiteCopy";
import AdminCaseStudies from "@/pages/admin/AdminCaseStudies";
import AdminServices from "@/pages/admin/AdminServices";
import AdminRoster from "@/pages/admin/AdminRoster";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminPacket from "@/pages/admin/AdminPacket";
import AdminFrameworks from "@/pages/admin/AdminFrameworks";
import AdminFieldNotes from "@/pages/admin/AdminFieldNotes";
import AdminScorecard from "@/pages/admin/AdminScorecard";
import AdminAudit from "@/pages/admin/AdminAudit";

function RouteEffects() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// Legal policies are hosted externally on Termly. These legacy routes redirect
// out to the corresponding Termly policy viewer.
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function Chrome({ children }) {
  return (
    <>
      <ExecHeader />
      <main>{children}</main>
      <ExecFooter />
    </>
  );
}

// Hub/Admin keep the legacy chrome — intentionally untouched by the
// Light Executive System rollout.
function HubChrome({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function Shell() {
  return (
    <div className="App">
      <RouteEffects />
      <Routes>
        {/* Public site with chrome */}
        <Route path="/" element={<Chrome><Home /></Chrome>} />
        <Route path="/work" element={<Chrome><Work /></Chrome>} />
        <Route path="/work/:slug" element={<Chrome><CaseStudyPage /></Chrome>} />
        <Route path="/services" element={<Chrome><Services /></Chrome>} />
        <Route path="/services/:sprintSlug" element={<Chrome><Sprint /></Chrome>} />
        <Route path="/public-sector" element={<Chrome><PublicSector /></Chrome>} />
        <Route path="/for/:segment" element={<Chrome><ForSegment /></Chrome>} />
        <Route path="/intake/:offer" element={<Chrome><Intake /></Chrome>} />
        <Route path="/about" element={<Chrome><About /></Chrome>} />
        <Route path="/refer" element={<Chrome><Refer /></Chrome>} />
        <Route path="/resources" element={<Chrome><Resources /></Chrome>} />
        <Route path="/resources/field-notes/:slug" element={<Chrome><FieldNote /></Chrome>} />
        <Route path="/resources/frameworks/:slug" element={<Chrome><FrameworkPage /></Chrome>} />
        <Route path="/scorecard" element={<Chrome><Scorecard /></Chrome>} />
        <Route path="/events" element={<Chrome><Events /></Chrome>} />
        <Route path="/events/:slug" element={<Chrome><EventDetail /></Chrome>} />
        <Route path="/the-table" element={<Chrome><TheTablePublic /></Chrome>} />
        <Route path="/search" element={<Chrome><Search /></Chrome>} />
        <Route path="/checkout/success" element={<Chrome><CheckoutSuccess /></Chrome>} />
        <Route path="/privacy" element={<ExternalRedirect to={LEGAL_LINKS.privacy} />} />
        <Route path="/terms" element={<ExternalRedirect to={LEGAL_LINKS.terms} />} />
        <Route path="/cookies" element={<ExternalRedirect to={LEGAL_LINKS.cookies} />} />
        <Route path="/login" element={<Chrome><Login /></Chrome>} />
        <Route path="/welcome" element={<Chrome><Welcome /></Chrome>} />

        {/* Executive Production microsite — off the main nav, its own shell */}
        <Route path="/executive-production" element={<ExecutiveProduction />} />

        {/* Executive system — unlinked design preview (own shell, not in nav/sitemap) */}
        <Route path="/executive-preview" element={<ExecutivePreview />} />

        {/* Hub (auth) */}
        <Route
          path="/hub"
          element={<ProtectedRoute><HubChrome><HubLayout /></HubChrome></ProtectedRoute>}
        >
          <Route index element={<Dashboard />} />
          <Route path="deliverables" element={<Deliverables />} />
          <Route path="packet" element={<PacketArchive />} />
          <Route path="events" element={<HubEvents />} />
          <Route path="the-table" element={<TheTable />} />
          <Route path="cohort" element={<Cohort />} />
          <Route path="account" element={<Account />} />
        </Route>

        {/* Admin (owner/admin) */}
        <Route
          path="/admin"
          element={<AdminRoute><HubChrome><AdminLayout /></HubChrome></AdminRoute>}
        >
          <Route index element={<AdminOverview />} />
          <Route path="crm" element={<AdminCRM />} />
          <Route path="site-copy" element={<AdminSiteCopy />} />
          <Route path="case-studies" element={<AdminCaseStudies />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="roster" element={<AdminRoster />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="frameworks" element={<AdminFrameworks />} />
          <Route path="field-notes" element={<AdminFieldNotes />} />
          <Route path="scorecard" element={<AdminScorecard />} />
          <Route path="packet" element={<AdminPacket />} />
          <Route path="audit" element={<AdminAudit />} />
        </Route>

        <Route path="*" element={<Chrome><NotFound /></Chrome>} />
      </Routes>
      <BriefModal />
      <Toaster />
    </div>
  );
}

function App() {
  useEffect(() => {
    captureUTM();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <BriefProvider>
          <Shell />
        </BriefProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
